/**
 * mSTEM SmartHome sensor sparkline (Phase 5 / SMH-02, DR-04 / RESEARCH
 * Pattern 5). Hand-rolled inline-SVG line chart — NO charting library.
 *
 * Maps a numeric series to a single green `<polyline>` scaled to the series
 * min/max, with ~4 horizontal gridlines and min/mid/max y-axis labels.
 * Pure presentational stateless component; the container owns the data +
 * the 2s tick that re-renders it.
 */
import PropTypes from 'prop-types';
import React from 'react';

import styles from './smarthome-dashboard-modal.css';

const W = 560;
const H = 160;
const PAD = 8;
const GRIDLINES = 4;

const SensorSparkline = ({series, unit}) => {
    const safe = (series && series.length) ? series : [0];
    const max = Math.max(...safe, 1);
    const min = Math.min(...safe, 0);
    const span = Math.max(max - min, 1);

    const points = safe.map((v, i) => {
        const x = PAD + ((i / Math.max(safe.length - 1, 1)) * (W - (2 * PAD)));
        const y = H - PAD - (((v - min) / span) * (H - (2 * PAD)));
        return `${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');

    // ~4 evenly spaced horizontal gridlines + a y-axis label per line.
    const lines = [];
    const labels = [];
    for (let g = 0; g <= GRIDLINES; g++) {
        const y = PAD + ((g / GRIDLINES) * (H - (2 * PAD)));
        const value = Math.round(max - ((g / GRIDLINES) * span));
        lines.push(
            <line
                key={`grid-${g}`}
                x1={PAD}
                y1={y.toFixed(1)}
                x2={W - PAD}
                y2={y.toFixed(1)}
                stroke="#EEF1FA"
                strokeWidth="1"
            />
        );
        labels.push(
            <text
                key={`label-${g}`}
                x="2"
                y={(y + 4).toFixed(1)}
                fontSize="11"
                fill="#162A75"
            >
                {value}{unit}
            </text>
        );
    }

    return (
        <svg
            className={styles.sparklineSvg}
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
        >
            {lines}
            <polyline
                fill="none"
                stroke="#2E9E5B"
                strokeWidth="2"
                strokeLinejoin="round"
                points={points}
            />
            {labels}
        </svg>
    );
};

SensorSparkline.propTypes = {
    series: PropTypes.arrayOf(PropTypes.number).isRequired,
    unit: PropTypes.string
};

SensorSparkline.defaultProps = {
    unit: ''
};

export default SensorSparkline;
